import {
  Box,
  Card,
  Divider,
  ReassignContactCard,
} from '@atgfr8/atg-ui-tailwind';
import {
  AccountCircleIcon,
  DomainIcon,
  EmailIcon,
  LoginIcon,
  PhoneIcon,
} from '@atgfr8/icons';
import { AutoComplete, Button } from '@components';
import { UserInfoModel } from '@store/orders';
import { UserAutoComplete } from '@typeDefinitions/userTypes';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { get } from 'lodash';
import { createEffect, createSignal, Show } from 'solid-js';

import { loadInformationClasses as classes } from '../carrier/loads/classes';

type ReassignProps = {
  type: string;
  contactInfo: UserInfoModel | undefined;
  isLoading: () => boolean;
  onButtonClick: (id: number, type: string) => Promise<void>;
  closePopover?: () => void;
  disabled?: boolean;
};

const ReassignCard = (props: ReassignProps) => {
  const [value, setValue] = createSignal('');
  const [autoCompleteValue, setAutoCompleteValue] = createSignal<
    UserInfoModel | undefined | null
  >();
  const [reassignLabel, setReassignLabel] = createSignal(
    'Assign New Covered By',
  );

  createEffect(() => {
    switch (props.type) {
      case 'COVERED BY':
        setReassignLabel('Assign New Covered By');
        break;
      case 'ASSIGNED TO':
        setReassignLabel('Assign New Order Rep');
        break;
      case 'CAPACITY OPS REP':
        setReassignLabel('Assign New Capacity Ops Rep');
        break;
      default:
        setReassignLabel('Assign New Covered By');
    }
  });

  const ReassignButton = () => (
    <Button
      variant="contained"
      label="Assign"
      onClick={async () => {
        await props.onButtonClick(
          autoCompleteValue()?.id as number,
          props.type,
        );
        if (props.closePopover !== undefined) props.closePopover();
      }}
      type="submit"
      class={classes.reassignButton}
      isLoading={props.isLoading()}
      disabled={!autoCompleteValue() || props.disabled}
      dataTestId="reassign-card-assign-btn"
    />
  );

  const handleAutoCompleteChange = (item: UserInfoModel | undefined | null) => {
    setAutoCompleteValue(item);
  };

  const handleValueCheck = (e: Event) => {
    const automCompleteValue = get(e.target, 'value');
    setValue(String(automCompleteValue));
  };
  return (
    <Box>
      <Show when={props.contactInfo}>
        <span class={classes.assignHeader}>
          Current <span>{props.type}</span>
        </span>
      </Show>
      <Box class="flex-col items-center p-[10px] pt-0 mobile:w-[90vw] nonMobile:w-[500px]">
        <ReassignContactCard
          contact={props.contactInfo}
          showIcon
          isLoading={props.isLoading()}
        />
        <Divider class={classes.divider} />
        <div class="group w-full">
          <div class="mb-0.5 text-[14px] font-normal text-[rgba(0,0,0,0.6)] group-focus-within:font-semibold group-focus-within:text-[#1B4960]">
            {reassignLabel()}
          </div>
          <AutoComplete
            endpoint="user/autocomplete?term"
            onItemSelect={(item) =>
              handleAutoCompleteChange(item as UserInfoModel)
            }
            id="auto-complete-reassign"
            placeholder="Search by name"
            class="w-full border-x-0! border-t-0! rounded-none! border-b! border-b-[rgba(0,0,0,0.42)]! hover:border-b-[rgba(0,0,0,0.87)]! focus-within:outline-none! focus-within:border-x-0! focus-within:border-t-0! focus-within:border-b-2! focus-within:border-b-[#1B4960]! [&>input]:pr-[104px]! [&>input::placeholder]:text-[rgba(0,0,0,0.38)]! [&:focus-within>input::placeholder]:text-[rgba(0,0,0,0.38)]! [&>.end-icon]:absolute [&>.end-icon]:right-0 [&>.end-icon]:top-1/2 [&>.end-icon]:z-[1] [&>.end-icon]:flex [&>.end-icon]:-translate-y-1/2 [&>.end-icon]:items-center [&>.end-icon]:justify-end"
            customButton={ReassignButton}
            defaultValue=""
            value={autoCompleteValue()?.name ?? ''}
            onInput={handleValueCheck}
            renderItemStyle={{
              padding: '3px',
              height: 'auto',
              'min-height': '100px',
            }}
            renderItem={(item) => {
              const user = item as UserAutoComplete;
              return (
                <Card
                  class={`${classes.cardStyle} shadow-none! border border-solid border-[rgba(0,0,0,0.23)] p-3 bg-white! mb-2`}
                >
                  <div class="flex flex-col gap-1 w-full border rounded-t-lg">
                    <span class="text-sm font-medium text-black! border-b  bg-[#eceff1] px-2 py-1 ">
                      {user.name}
                    </span>
                    <div class="flex flex-col px-2 gap-1 py-1 relative">
                      <div class="flex items-center gap-2 text-base! text-[#51607b]">
                        <DomainIcon class="text-[#026EA1] text-base!" />
                        <span class="font-semibold text-sm!">
                          {user.office}
                        </span>
                      </div>
                      <Show when={user.phone1}>
                        <div class="flex items-center gap-2 text-base! text-[#51607b]">
                          <PhoneIcon class="text-[#026EA1] text-base!" />
                          <a
                            href={`tel:${user.phone1Ext}${user.phone1}`}
                            class="text-[#026EA1] no-underline text-sm! break-all"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {formatPhoneNumber(user.phone1)} {user.phone1Ext}
                          </a>
                        </div>
                      </Show>
                      <Show when={user.email}>
                        <div class="flex items-center gap-2 text-base! text-[#51607b]">
                          <EmailIcon class="text-[#026EA1] text-base!" />
                          <a
                            href={`mailto:${user.email}`}
                            class="text-[#026EA1] hover:underline text-sm! break-all"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {user.email}
                          </a>
                        </div>
                      </Show>
                      <LoginIcon class=" absolute top-[50%] translate-y-[-50%] right-4 z-10 text-[#026EA1]" />
                    </div>
                  </div>
                </Card>
              );
            }}
            startAdornment={
              autoCompleteValue() ? (
                <AccountCircleIcon class={classes.accountBlue} />
              ) : value() ? null : (
                <AccountCircleIcon class={classes.accountGrey} />
              )
            }
          />
        </div>
      </Box>
    </Box>
  );
};

export default ReassignCard;
